/**
 * Chunk scroll + recycle math (pure) — shared by the road (render/world.ts) and
 * the roadside content chunks (render/biomes.ts) so both scroll in the same
 * direction with the same window.
 *
 * Convention (see TECH_STACK.md): the player stands still at z = 0 facing -Z.
 * AHEAD = negative z. Static world content must therefore stream toward +Z as
 * `player.distance` grows — the world moves past the camera.
 *
 * A chunk is anchored by a ROAD DISTANCE (not a world z): anchor `a` is the
 * travel distance at which the chunk's near edge reaches the player. The chunk
 * covers road-distance [a, a + chunkLength], so for a player at distance D:
 *
 *   near edge (largest z)   z = D - a
 *   far  edge (most ahead)  z = D - a - chunkLength
 *
 * z therefore INCREASES with D: the chunk streams toward +Z, past the camera.
 * Recycling only ever adds/subtracts whole ring spans to the anchor, so a chunk
 * moves exactly once per traversal and the dash/prop pattern never slides.
 *
 * Window: near edge z is kept in (-chunksAhead*L, +chunksBehind*L]. A chunk is
 * sent back to the front as soon as its NEAR edge has passed chunksBehind*L
 * behind the camera — its remaining tail is behind the camera and invisible,
 * and recycling at that moment keeps a full chunksAhead*L of road in front of
 * the camera at every instant (recycling on the far edge instead would let the
 * ahead extent sag by one chunk length just before each recycle).
 *
 * Zero allocation: every function is pure arithmetic over numbers.
 */

/** Slack (m) so float error never re-triggers a recycle on the same frame. */
const EPS = 1e-3;

/**
 * Road-distance anchor for chunk slot `i` at run start (distance 0): slots
 * [0, chunksBehind) start behind the camera, the rest lay out ahead.
 */
export function initialAnchor(i: number, chunkLength: number, chunksBehind: number): number {
  return (i - chunksBehind) * chunkLength;
}

/** Render z of the chunk's near (largest-z) edge for a player at `distance`. */
export function chunkNearZ(anchor: number, distance: number): number {
  return distance - anchor;
}

/**
 * Ring-shift `anchor` by whole spans until the chunk sits inside the window.
 * Returns the (possibly unchanged) anchor; callers compare against the old
 * value to detect a recycle. Normally at most one iteration — the loops only
 * spin when `distance` jumps (a run reset).
 */
export function recycleAnchor(
  anchor: number,
  distance: number,
  chunkLength: number,
  chunkCount: number,
  chunksBehind: number,
): number {
  const span = chunkCount * chunkLength;
  const behindLimit = chunksBehind * chunkLength;
  const aheadLimit = -(chunkCount - chunksBehind) * chunkLength;
  let a = anchor;
  // Passed behind the camera: send it to the front of the ahead window.
  while (distance - a > behindLimit + EPS) a += span;
  // Beyond the ahead window (only reachable when distance jumps backwards).
  while (distance - a < aheadLimit - EPS) a -= span;
  return a;
}
